/* ==========================================================================
   GYM STUDIO — usage tracking
   ==========================================================================
   Listens for the `ctx:gym-studio:track` events ctx-gym-studio-ui.js fires on
   every meaningful action (add, remove, guard rail hit, quiz complete, add to
   cart) and logs one row per session to Airtable via an n8n webhook, upserted
   on Session ID so repeated sends just update the same row.

   Owns everything ctx-gym-studio-ui.js should not have to know about:
   the session id, how long they have been on the page, what device this is,
   and how/when to actually send. That file only ever describes what
   happened; this file decides when that is worth mailing home.

   Sends:
     - right after a successful Add to Cart (the highest-value signal — do
       not wait for them to leave the tab to record it)
     - whenever the tab is hidden or the page is being left (visibilitychange
       / pagehide), via sendBeacon so it survives the page going away

   Built 12 Aug 2026. Webhook: n8n workflow "Log Gym Builder Session".

   ── 15 Sep 2026 ──────────────────────────────────────────────────────────
   The Airtable table and the n8n workflow were both extended to 36 fields,
   but this file was never updated to send the extra ones — so across 2,029
   logged sessions, Furthest Step, Seconds To First Action, Event Trail,
   Viewport Width, Tools Used, Room Shape and Usable Sqft were empty on every
   single row. n8n was already mapping all of them; nothing was arriving.

   This file now derives and sends those seven. Two of them (room_shape,
   usable_sqft) were being produced by the UI all along and simply dropped
   here. The other five are derived in this file from the event stream, so
   the 316KB UI file did not need to change.

   Deliberately still NOT sent, because the UI does not produce them and
   inventing them here would be worse than leaving them blank:
     seed_handle, seed_title, seed_survived, catalog_count, search_count,
     search_terms, browse_all_opened, items_not_fitting, coverage_gaps,
     cart_type.
   Those need a `track()` payload change in ctx-gym-studio-ui.js.
   ========================================================================== */
(function () {
  'use strict';

  var ENDPOINT = 'https://buildagym.app.n8n.cloud/webhook/gym-builder-session';
  var SID_KEY = 'ctxGymSession.id';
  var START_KEY = 'ctxGymSession.startedAt';
  /* Persisted so a mid-session reload cannot reset them. The counters the UI
     owns (machines_added/removed/guard_count) still reset on reload — that is
     a known v1 limitation and needs fixing in the UI file, not here. */
  var STEP_KEY = 'ctxGymSession.stepRank';
  var FIRST_KEY = 'ctxGymSession.firstActionAt';
  var TOOLS_KEY = 'ctxGymSession.tools';
  var TRAIL_KEY = 'ctxGymSession.trail';

  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }

  var sessionId = ssGet(SID_KEY);
  if (!sessionId) {
    sessionId = 's-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    ssSet(SID_KEY, sessionId);
  }

  var startedAt = ssGet(START_KEY);
  if (!startedAt) {
    startedAt = new Date().toISOString();
    ssSet(START_KEY, startedAt);
  }
  var startedAtMs = new Date(startedAt).getTime();

  var device = window.matchMedia && window.matchMedia('(max-width: 799px)').matches ? 'Mobile' : 'Desktop';

  /* ── FUNNEL POSITION ──────────────────────────────────────────────────────
     Ranked so the furthest point reached can only ever go up, never back down
     when a later event is a lesser one. Names must match the Airtable single
     select exactly or the cell silently stays empty. */
  var STEPS = ['Arrived', 'Opened quiz', 'Completed quiz', 'Edited the build', 'Reached cart'];
  var STEP_OF = {
    'init': 0,
    'plan-resumed': 0,
    'quiz-open': 1,
    'quiz': 2,
    'add': 3, 'remove': 3, 'layout-improved': 3, 'plan-measured': 3,
    'scenario-saved': 3, 'scenario-loaded': 3, 'plan-reviewed': 3,
    'email-opened': 3, 'email-sent': 3, 'plan-shared': 3, 'share-link-copied': 3,
    'guard': 3,
    'add-to-cart': 4
  };

  /* Which named tools someone actually touched — the thing that tells us
     whether a feature we built is being used at all. */
  var TOOL_OF = {
    'plan-measured': 'Measure',
    'layout-improved': 'Auto-layout',
    'scenario-saved': 'Save scenario',
    'scenario-loaded': 'Load scenario',
    'email-opened': 'Email plan',
    'email-sent': 'Email plan',
    'plan-shared': 'Share',
    'share-link-copied': 'Share',
    'plan-reviewed': 'Review'
  };

  var stepRank = parseInt(ssGet(STEP_KEY), 10);
  if (!(stepRank >= 0)) stepRank = 0;

  var firstActionAt = parseInt(ssGet(FIRST_KEY), 10);
  if (!(firstActionAt > 0)) firstActionAt = 0;

  var tools = {};
  try {
    var savedTools = JSON.parse(ssGet(TOOLS_KEY) || '[]');
    for (var i = 0; i < savedTools.length; i++) tools[savedTools[i]] = true;
  } catch (e) {}

  var trail = [];
  try { trail = JSON.parse(ssGet(TRAIL_KEY) || '[]'); } catch (e) {}

  var latest = null;

  function elapsed() { return Math.max(0, Math.round((Date.now() - startedAtMs) / 1000)); }

  function note(type) {
    /* funnel position — highest wins */
    var rank = STEP_OF[type];
    if (rank > stepRank) { stepRank = rank; ssSet(STEP_KEY, String(stepRank)); }

    /* time to first real action: 'init' is the page loading, not a decision */
    if (!firstActionAt && type !== 'init') {
      firstActionAt = elapsed() || 1;
      ssSet(FIRST_KEY, String(firstActionAt));
    }

    /* named tools */
    var tool = TOOL_OF[type];
    if (tool && !tools[tool]) {
      tools[tool] = true;
      ssSet(TOOLS_KEY, JSON.stringify(Object.keys(tools)));
    }

    /* the trail, newest last. Capped: this is a multiline text cell, not a log
       sink, and a long fiddling session would otherwise send kilobytes on every
       visibilitychange. */
    trail.push(elapsed() + 's ' + type);
    if (trail.length > 40) trail = trail.slice(trail.length - 40);
    ssSet(TRAIL_KEY, JSON.stringify(trail));
  }

  function send() {
    if (!latest) return;
    var payload = {
      session_id: sessionId,
      started_at: startedAt,
      duration_seconds: elapsed(),
      device: device,
      entry_point: latest.entry_point,
      room_type: latest.room_type,
      room_w: latest.room_w,
      room_d: latest.room_d,
      budget: latest.budget,
      quiz_completed: latest.quiz_completed,
      machines_added: latest.machines_added,
      machines_removed: latest.machines_removed,
      final_count: latest.final_count,
      final_list: latest.final_list,
      guard_count: latest.guard_count,
      reached_cart: latest.reached_cart,
      cart_value: latest.cart_value,
      last_action: latest.last_action,

      /* 19 Oct 2026: the _bundle_id stamped on the cart lines (and the
         'Gym bundle id' cart attribute), so a session row can be matched to its
         order or abandoned checkout. Blank until they add to cart. */
      bundle_id: latest.bundle_id || '',

      /* produced by the UI all along, previously dropped here */
      room_shape: latest.room_shape,
      usable_sqft: latest.usable_sqft,

      /* derived in this file from the event stream */
      furthest_step: STEPS[stepRank] || 'Arrived',
      seconds_to_first_action: firstActionAt || null,
      viewport_w: window.innerWidth || null,
      tools_used: Object.keys(tools).join(', '),
      event_trail: trail.join('\n')
    };

    /* Not sendBeacon: it fixes its request's credentials mode to 'include',
       and n8n's webhook does not answer the resulting preflight with
       Access-Control-Allow-Credentials, so the send silently fails. A plain
       fetch sends no credentials cross-origin, which the preflight allows —
       and `keepalive` lets it survive the page going away same as a beacon
       would. */
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        mode: 'cors'
      });
    } catch (e) {}
  }

  document.addEventListener('ctx:gym-studio:track', function (e) {
    latest = e.detail;
    try { note(e.detail.type); } catch (err) {}
    /* These actions represent a choice to preserve or continue the plan. Send
       them immediately so a later tab close cannot erase the strongest signals. */
    var immediate = {
      'add-to-cart': true,
      'plan-shared': true,
      'share-link-copied': true,
      'email-sent': true,
      'plan-resumed': true
    };
    if (immediate[e.detail.type]) send();
  });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) send();
  });
  window.addEventListener('pagehide', send);
})();
